import React, { useEffect, useState } from "react";
import { useAuth } from "../../core/context/AuthContext";
import { isTaskMantisDesktop } from "../desktopUpdate";
import { CAPTURE_HOTKEY_LABEL } from "./shared/captureHotkeyTip";
import {
  bindingFromKeyboardEvent,
  formatBindingLabel,
} from "./shared/keyboardShortcuts";
import { useCaptureHotkeyState } from "./shared/useCaptureHotkeyState";
import { useKeyboardShortcutsPreference } from "./shared/useKeyboardShortcutsPreference";

/**
 * Settings panel to view and remap keyboard shortcuts.
 * Click a key chip to rebind; Esc cancels listening.
 *
 * @example
 * <ActionItemsSettingsKeyboardPanel />
 */
const ActionItemsSettingsKeyboardPanel = () => {
  const { user } = useAuth();
  const { bindings, definitions, setBinding, resetBinding, resetAll, overrides } =
    useKeyboardShortcutsPreference(user);
  const { state: captureHotkeyState } = useCaptureHotkeyState();
  const [listeningId, setListeningId] = useState("");
  const [errorById, setErrorById] = useState({});

  useEffect(() => {
    if (!listeningId) return undefined;

    const handleKeyDown = (event) => {
      event.preventDefault();
      event.stopPropagation();

      if (event.key === "Escape") {
        setListeningId("");
        return;
      }

      const nextBinding = bindingFromKeyboardEvent(event);
      if (!nextBinding) return;

      const result = setBinding(listeningId, nextBinding);
      if (!result.ok) {
        setErrorById((prev) => ({ ...prev, [listeningId]: result.error }));
        return;
      }
      setErrorById((prev) => {
        const next = { ...prev };
        delete next[listeningId];
        return next;
      });
      setListeningId("");
    };

    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
  }, [listeningId, setBinding]);

  const hasOverrides = Object.keys(overrides).length > 0;
  const systemShortcuts = definitions.filter((entry) => !entry.editable);
  const customShortcuts = definitions.filter((entry) => entry.editable);

  const startListening = (shortcutId) => {
    setErrorById((prev) => {
      const next = { ...prev };
      delete next[shortcutId];
      return next;
    });
    setListeningId((current) => (current === shortcutId ? "" : shortcutId));
  };

  const renderRow = (definition) => {
    const isListening = listeningId === definition.id;
    const binding = bindings[definition.id] || definition.defaultBinding;
    const isCustomized = Boolean(overrides[definition.id]);
    const error = errorById[definition.id] || "";
    const keyLabel = isListening ? "…" : formatBindingLabel(binding);

    return (
      <div
        key={definition.id}
        className={`keyboard-shortcuts-row${
          isListening ? " keyboard-shortcuts-row--listening" : ""
        }`}
        title={definition.description}
      >
        <div className="keyboard-shortcuts-row__copy">
          <span className="keyboard-shortcuts-row__label">{definition.label}</span>
          {error ? <span className="keyboard-shortcuts-row__error">{error}</span> : null}
        </div>
        <div className="keyboard-shortcuts-row__controls">
          {isCustomized ? (
            <button
              type="button"
              className="keyboard-shortcuts-row__reset"
              onClick={() => {
                resetBinding(definition.id);
                setListeningId("");
              }}
            >
              Reset
            </button>
          ) : null}
          {definition.editable ? (
            <button
              type="button"
              className={`keyboard-shortcuts-row__kbd keyboard-shortcuts-row__kbd--editable${
                isListening ? " keyboard-shortcuts-row__kbd--listening" : ""
              }`}
              onClick={() => startListening(definition.id)}
              aria-label={
                isListening
                  ? `Press a new key for ${definition.label}`
                  : `Change shortcut for ${definition.label}, currently ${formatBindingLabel(binding)}`
              }
            >
              {keyLabel}
            </button>
          ) : (
            <kbd
              className="keyboard-shortcuts-row__kbd"
              aria-label={`${definition.label}: ${formatBindingLabel(binding)} (system)`}
            >
              {formatBindingLabel(binding)}
            </kbd>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="keyboard-shortcuts-panel settings-general-section">
      <p className="settings-general-section__desc">
        Click a key to remap it. System shortcuts stay fixed.
      </p>

      {isTaskMantisDesktop() ? (
        <div className="keyboard-shortcuts-group" role="list" aria-label="Desktop shortcuts">
          <h3 className="keyboard-shortcuts-group__title">Desktop</h3>
          <div
            className="keyboard-shortcuts-row"
            title="Select text in any app, then press this to add it as a Task Mantis task"
          >
            <div className="keyboard-shortcuts-row__copy">
              <span className="keyboard-shortcuts-row__label">
                Quick capture (works in any app)
              </span>
              {captureHotkeyState.enabled ? null : (
                <span className="keyboard-shortcuts-row__error">
                  Off — turn it on under General → Quick capture
                </span>
              )}
            </div>
            <div className="keyboard-shortcuts-row__controls">
              <kbd
                className="keyboard-shortcuts-row__kbd"
                aria-label={`Quick capture: ${CAPTURE_HOTKEY_LABEL}, works system-wide`}
              >
                {CAPTURE_HOTKEY_LABEL}
              </kbd>
            </div>
          </div>
        </div>
      ) : null}

      <div className="keyboard-shortcuts-group" role="list" aria-label="System shortcuts">
        <h3 className="keyboard-shortcuts-group__title">System</h3>
        {systemShortcuts.map(renderRow)}
      </div>

      <div className="keyboard-shortcuts-group" role="list" aria-label="Customizable shortcuts">
        <div className="keyboard-shortcuts-group__header">
          <h3 className="keyboard-shortcuts-group__title">Customizable</h3>
          {hasOverrides ? (
            <button
              type="button"
              className="keyboard-shortcuts-row__reset"
              onClick={() => {
                resetAll();
                setListeningId("");
                setErrorById({});
              }}
            >
              Reset all
            </button>
          ) : null}
        </div>
        {customShortcuts.map(renderRow)}
      </div>
    </div>
  );
};

export default ActionItemsSettingsKeyboardPanel;
